import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { 
  QrCode, 
  CheckCircle, 
  XCircle, 
  Loader2,
  ArrowRight,
  PartyPopper
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
      setMessage('Session invalide');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/payment-status/${sessionId}`);
      if (response.data.status === 'paid') {
        setStatus('success');
        setMessage(response.data.message);
      } else {
        // Retry after a short delay
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Erreur lors de la vérification du paiement');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 text-orange-500 animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
              Vérification du paiement...
            </h2>
            <p className="text-gray-500">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
            <Skeleton className="h-4 w-3/4 mx-auto mt-4" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
              Échec du paiement
            </h2>
            <p className="text-gray-500 mb-6">
              {message}
            </p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
            >
              Réessayer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-xl">
        <CardContent className="p-8 text-center">
          <div className="relative mb-6">
            <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <PartyPopper className="h-8 w-8 text-orange-500 absolute top-0 right-1/4 animate-bounce" />
          </div>
          
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
            Bienvenue sur ZOTMENU !
          </h2>
          
          <p className="text-gray-500 mb-6">
            {message || 'Votre compte a été activé. Votre essai gratuit de 2 mois a commencé !'}
          </p>

          <div className="bg-orange-50 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-2">Prochaines étapes :</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-orange-500" />
                Connectez-vous à votre compte
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-orange-500" />
                Ajoutez vos plats et catégories
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-orange-500" />
                Personnalisez votre menu
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-orange-500" />
                Partagez votre QR code
              </li>
            </ul>
          </div>

          <Button
            onClick={() => navigate('/login')}
            className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold"
            data-testid="goto-login-btn"
          >
            Se connecter
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <Link to="/" className="block mt-4 text-gray-500 hover:text-gray-700 text-sm">
            Retour à l'accueil
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
